import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import Dropdown from '../Dropdown';
import SearchableClientDropdown from '../SearchableClientDropdown';
import SearchableContactDropdown from '../SearchableContactDropdown';
import NewContactCard from '../NewContactCard';
import ContactInfoButton from '../ContactInfoButton';
import { PROJECT_JOB_TYPE_OPTIONS, stageContactInheritLabel } from '../ProjectDetailModal/config';
import { useUserRole } from '../../utils/useUserRole';
import { canUseManagerOnlyStatuses, workflowStatusDropdownItems } from '../../utils/workflowStatusAccess';
import './general-info-panel.scss';
import Skeleton from '../Skeleton';
import { errorMessage } from '../../utils/supabaseErrors';

interface GeneralInfoPanelProps {
  projectId: number;
  stageId: number | null;
  onChange?: () => void;
}

interface ProjectInfo {
  id: number;
  types: string;
  workflow_status_id: number | null;
  client_id: string | null;
  client_name: string | null;
  primary_contact_id: string | null;
  primary_contact_name: string | null;
}

interface StageInfo {
  id: number;
  workflow_status_id: number | null;
  /** The stage's own contact. Null means it inherits the project's. */
  primary_contact_id: string | null;
}

interface WorkflowStatusOption {
  id: number;
  status_name: string;
  display_order: number;
}

interface ClientRefRow {
  id: number;
  company_name: string;
}

interface ContactRefRow {
  id: number;
  first_name: string;
  last_name: string;
}

interface ProjectJoinRow {
  id: number;
  types: string;
  workflow_status_id: number | null;
  client: number | null;
  primary_contact: number | null;
  client_ref: ClientRefRow | ClientRefRow[] | null;
  contact_ref: ContactRefRow | ContactRefRow[] | null;
}

const GeneralInfoPanel: React.FC<GeneralInfoPanelProps> = ({ projectId, stageId, onChange }) => {
  const supabase = useSupabaseClient();
  const canUseManagerOnly = canUseManagerOnlyStatuses(useUserRole());

  const [project, setProject] = useState<ProjectInfo | null>(null);
  const [stage, setStage] = useState<StageInfo | null>(null);
  const [workflowStatuses, setWorkflowStatuses] = useState<WorkflowStatusOption[]>([]);
  const [isSaving, setIsSaving] = useState(false);
  // The inline "+ Add contact" form under Primary Contact, and a key that
  // remounts the contact dropdown so it lists a contact just created.
  const [isAddingContact, setIsAddingContact] = useState(false);
  const [contactRefreshKey, setContactRefreshKey] = useState(0);
  const [error, setError] = useState<string | null>(null);

  const fetchProject = useCallback(async () => {
    const { data, error: queryError } = await supabase
      .from('projects')
      .select(
        'id, types, workflow_status_id, client, primary_contact, ' +
          'client_ref:clients!projects_client_fkey(id, company_name), ' +
          'contact_ref:contacts!projects_primary_contact_fkey(id, first_name, last_name)'
      )
      .eq('id', projectId)
      .single();

    if (queryError) {
      console.error('Error fetching project info:', queryError);
      setError(queryError.message);
      return;
    }

    const row = data as unknown as ProjectJoinRow;
    const contact = Array.isArray(row.contact_ref) ? row.contact_ref[0] : row.contact_ref;
    const client = Array.isArray(row.client_ref) ? row.client_ref[0] : row.client_ref;

    setProject({
      id: row.id,
      types: row.types,
      workflow_status_id: row.workflow_status_id,
      client_id: row.client != null ? String(row.client) : null,
      client_name: client?.company_name ?? null,
      primary_contact_id: row.primary_contact != null ? String(row.primary_contact) : null,
      primary_contact_name: contact ? `${contact.first_name} ${contact.last_name}` : null,
    });
  }, [supabase, projectId]);

  const fetchStage = useCallback(async () => {
    if (!stageId) {
      setStage(null);
      return;
    }
    const { data, error: queryError } = await supabase
      .from('project_stages')
      .select('id, workflow_status_id, primary_contact')
      .eq('id', stageId)
      .single();

    if (queryError) {
      console.error('Error fetching stage info:', queryError);
      setError(queryError.message);
      return;
    }

    setStage({
      id: data.id,
      workflow_status_id: data.workflow_status_id,
      primary_contact_id: data.primary_contact != null ? String(data.primary_contact) : null,
    });
  }, [supabase, stageId]);

  const fetchWorkflowStatuses = useCallback(async (jobType: string) => {
    const { data, error: rpcError } = await supabase.rpc('get_workflow_statuses_for_type', {
      p_job_type: jobType,
    });
    if (rpcError) {
      console.error('Error fetching workflow statuses:', rpcError);
      return;
    }
    setWorkflowStatuses((data || []) as WorkflowStatusOption[]);
  }, [supabase]);

  useEffect(() => {
    fetchProject();
  }, [fetchProject]);

  useEffect(() => {
    fetchStage();
  }, [fetchStage]);

  useEffect(() => {
    if (project?.types) {
      fetchWorkflowStatuses(project.types);
    }
  }, [project?.types, fetchWorkflowStatuses]);

  const saveProjectField = useCallback(
    async (updates: Record<string, string | number | null>) => {
      setIsSaving(true);
      setError(null);
      try {
        const { data, error: rpcError } = await supabase.rpc('update_project_detail', {
          project_id: projectId,
          project_data: updates,
        });
        if (rpcError) throw new Error(rpcError.message);
        if (data && !data.success) throw new Error(data.error || 'Failed to save');
        await fetchProject();
        onChange?.();
      } catch (err) {
        console.error('Error saving project field:', err);
        setError(err instanceof Error ? err.message : 'Failed to save');
      } finally {
        setIsSaving(false);
      }
    },
    [supabase, projectId, fetchProject, onChange]
  );

  // A stage's own fields go through update_project_stage, which builds its SQL
  // from the keys it is sent: send exact column names only.
  const saveStageField = useCallback(
    async (updates: Record<string, string | number | null>) => {
      if (!stageId) return;
      setIsSaving(true);
      setError(null);
      try {
        const { data, error: rpcError } = await supabase.rpc('update_project_stage', {
          p_stage_id: stageId,
          p_stage_data: updates,
        });
        if (rpcError) throw rpcError;
        if (data && !data.success) throw new Error(data.error || 'Failed to save');
        await fetchStage();
        onChange?.();
      } catch (err) {
        console.error('Error saving stage field:', err);
        setError(errorMessage(err, 'Failed to save'));
      } finally {
        setIsSaving(false);
      }
    },
    [supabase, stageId, fetchStage, onChange]
  );

  const handleClientChange = useCallback(
    (clientId: string | number | null) => {
      const next = clientId != null && clientId !== '' ? Number(clientId) : null;
      // A form opened for the old client would create its contact there.
      setIsAddingContact(false);
      saveProjectField({ client: next, primary_contact: null });
    },
    [saveProjectField]
  );

  const handleContactChange = useCallback(
    (contactId: string | number | null) => {
      const next = contactId != null && contactId !== '' ? Number(contactId) : null;
      // On a stage, null puts it back to inheriting the project's contact.
      if (stageId) saveStageField({ primary_contact: next });
      else saveProjectField({ primary_contact: next });
    },
    [stageId, saveStageField, saveProjectField]
  );

  // Shown at once, as the contractor fields do: otherwise the remounted
  // dropdown shows the previous contact until the save's refetch returns.
  const handleContactCreated = useCallback(
    (contactId: string) => {
      setIsAddingContact(false);
      setContactRefreshKey((k) => k + 1);
      if (stageId) setStage((prev) => (prev ? { ...prev, primary_contact_id: contactId } : prev));
      else setProject((prev) => (prev ? { ...prev, primary_contact_id: contactId } : prev));
      handleContactChange(contactId);
    },
    [stageId, handleContactChange]
  );

  const handleCancelAddContact = useCallback(() => setIsAddingContact(false), []);

  const handleTypeChange = useCallback(
    (value: string) => {
      saveProjectField({ types: value });
    },
    [saveProjectField]
  );

  const handleWorkflowStatusChange = useCallback(
    async (value: string) => {
      const itemId = stageId ?? projectId;
      const entityType: 'project' | 'stage' = stageId ? 'stage' : 'project';
      if (!value) return;

      setIsSaving(true);
      setError(null);
      try {
        const { data, error: rpcError } = await supabase.rpc('update_workflow_status', {
          p_item_id: itemId,
          p_entity_type: entityType,
          p_new_status_id: parseInt(value, 10),
        });
        if (rpcError) throw new Error(rpcError.message);
        if (data && !data.success) throw new Error(data.error || 'Failed to update status');

        if (stageId) await fetchStage();
        else await fetchProject();
        onChange?.();
      } catch (err) {
        console.error('Error updating workflow status:', err);
        setError(err instanceof Error ? err.message : 'Failed to update status');
      } finally {
        setIsSaving(false);
      }
    },
    [supabase, stageId, projectId, fetchStage, fetchProject, onChange]
  );

  const typeDropdownItems = useMemo(
    () =>
      PROJECT_JOB_TYPE_OPTIONS.map((t) => ({
        id: t,
        label: t,
        value: t,
      })),
    []
  );

  // Effective workflow status: stage uses its own (falls back to project's), project uses its own.
  const currentWorkflowStatusId = useMemo(() => {
    if (!workflowStatuses.length) return '';
    if (stageId) {
      const stageVal = stage?.workflow_status_id;
      if (stageVal != null && workflowStatuses.some((ws) => ws.id === stageVal)) {
        return String(stageVal);
      }
      const projVal = project?.workflow_status_id;
      if (projVal != null && workflowStatuses.some((ws) => ws.id === projVal)) {
        return String(projVal);
      }
      return String(workflowStatuses[0].id);
    }
    const projVal = project?.workflow_status_id;
    if (projVal != null && workflowStatuses.some((ws) => ws.id === projVal)) {
      return String(projVal);
    }
    return String(workflowStatuses[0].id);
  }, [workflowStatuses, stage?.workflow_status_id, project?.workflow_status_id, stageId]);

  // A stage has no job type of its own; `project` is always its parent here.
  // Exempt the SAVED status, not currentWorkflowStatusId: for a stage with no
  // status of its own that is the project's, which the trigger does not see.
  const savedWorkflowStatusId = stageId ? stage?.workflow_status_id : project?.workflow_status_id;
  const workflowStatusItems = useMemo(
    () => workflowStatusDropdownItems(workflowStatuses, project?.types, canUseManagerOnly, savedWorkflowStatusId),
    [workflowStatuses, project?.types, canUseManagerOnly, savedWorkflowStatusId]
  );

  // On a stage, wait for the stage too: its contact field would otherwise
  // show "Inherit from project" until the stage's own contact arrived.
  if (!project || (stageId && stage?.id !== stageId)) {
    // A failed fetch never sets `project`, so without this branch the panel
    // sat on its loading state forever with the error unrendered below.
    if (error) return <div className="general-info-panel__error">{error}</div>;
    return (
      <div className="general-info-panel" aria-busy="true">
        <div className="general-info-panel__section">
          {[0, 1].map((r) => (
            <div key={r} className="general-info-panel__row two-col">
              {[0, 1].map((c) => (
                <div key={c} className="general-info-panel__field">
                  <Skeleton width={72} height={11} />
                  <Skeleton variant="rectangular" height={34} borderRadius={8} />
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    );
  }

  // On a stage, the stage's own contact (null = inheriting the project's).
  const contactValue = stageId ? stage?.primary_contact_id ?? null : project.primary_contact_id ?? null;

  return (
    <div className="general-info-panel">
      {error && <div className="general-info-panel__error">{error}</div>}

      <div className="general-info-panel__section">
        <div className="general-info-panel__row two-col">
          <div className="general-info-panel__field">
            <label className="general-info-panel__label">Client</label>
            <div className="general-info-panel__input">
              {stageId ? (
                <div className="general-info-panel__readonly">
                  {project.client_name || 'No client assigned'}
                </div>
              ) : (
                <SearchableClientDropdown
                  value={project.client_id ?? null}
                  onChange={handleClientChange}
                  placeholder="Select client..."
                  allowNone
                  noneLabel="None"
                  disabled={isSaving}
                />
              )}
            </div>
          </div>
          <div className="general-info-panel__field">
            <div className="general-info-panel__label-row">
              <label className="general-info-panel__label">Primary Contact</label>
              {/* As on the contractor contacts in the PEP and Construction Admin. */}
              {project.client_id && (
                <button
                  type="button"
                  className="panel-heading__action"
                  onClick={() => setIsAddingContact((open) => !open)}
                  disabled={isSaving}
                >
                  {isAddingContact ? 'Cancel' : '+ Add contact'}
                </button>
              )}
            </div>
            <div className="general-info-panel__input">
              {/* The dropdown with the contact-info card beside it, as on the
                  contractor contacts. On a stage it edits the stage's own
                  contact, from the project's client; left empty, the stage
                  inherits the project's. */}
              <div className="contact-field">
                <SearchableContactDropdown
                  key={contactRefreshKey}
                  value={contactValue}
                  onChange={handleContactChange}
                  placeholder={project.client_id ? 'Select contact...' : 'Select a company first'}
                  allowNone
                  noneLabel={stageId ? stageContactInheritLabel(project.primary_contact_name) : 'None'}
                  clientId={project.client_id ? parseInt(project.client_id, 10) : undefined}
                  disabled={!project.client_id || isSaving}
                />
                <ContactInfoButton contactId={contactValue} />
              </div>
              {isAddingContact && project.client_id && (
                <NewContactCard
                  clientId={parseInt(project.client_id, 10)}
                  onCancel={handleCancelAddContact}
                  onCreated={handleContactCreated}
                />
              )}
            </div>
          </div>
        </div>

        <div className="general-info-panel__row two-col">
          <div className="general-info-panel__field">
            <label className="general-info-panel__label">Type</label>
            <div className="general-info-panel__input">
              {stageId ? (
                <div className="general-info-panel__readonly">{project.types}</div>
              ) : (
                <Dropdown
                  items={typeDropdownItems}
                  value={project.types}
                  onChange={handleTypeChange}
                  placeholder="Select type..."
                  searchable={false}
                  disabled={isSaving}
                />
              )}
            </div>
          </div>
          <div className="general-info-panel__field">
            <label className="general-info-panel__label">Status</label>
            <div className="general-info-panel__input">
              {workflowStatuses.length === 0 ? (
                <Skeleton variant="rectangular" height={34} borderRadius={8} />
              ) : (
                <Dropdown
                  key={`status-${stageId ?? projectId}-${workflowStatuses.length}`}
                  items={workflowStatusItems}
                  value={currentWorkflowStatusId}
                  onChange={handleWorkflowStatusChange}
                  placeholder="Select status..."
                  searchable={false}
                  disabled={isSaving}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralInfoPanel;
